import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

type ComponentType = {
  id: string | number
  children: (dragHandleProps: {
    attributes: React.HTMLAttributes<HTMLElement>
    listeners: Record<string, Function> | undefined
  }) => React.ReactNode
}

/**
 * DraggableItemVirtualised - Wrapper for items in virtualized sortable lists
 *
 * Uses render prop pattern to pass drag handle props to children,
 * allowing them to control where the drag handle appears.
 *
 * Example usage:
 * ```tsx
 * <DraggableItemVirtualised id={item.id}>
 *   {(dragHandleProps) => (
 *     <ItemComponent dragHandleProps={dragHandleProps} />
 *   )}
 * </DraggableItemVirtualised>
 * ```
 */
export const DraggableItemVirtualised: React.FC<ComponentType> = ({
  children,
  id,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const dragHandleProps = {
    attributes,
    listeners,
  }

  return (
    <div ref={setNodeRef} style={style}>
      {children(dragHandleProps)}
    </div>
  )
}
